import { normalizeUTCDateString } from "~/utils/DateFormatter";

type HashMap = {
  [articleId: string]: {
    title: string;
    summary: string;
  };
};

const hashCache: HashMap = {};
const TITLE_WEIGHT = 0.7;
const SUMMARY_WEIGHT = 0.3;
const GROUP_THRESHOLD = 0.6;

const computeHashText = (str: string) => {
  const text = str || "";
  const hashText = text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (hashText === "") {
    return text.replace(/\s/g, "");
  }

  return hashText;
};

const calculateJaccardSimilarity = function (str1: string, str2: string) {
  const set1 = new Set(str1.split(" "));
  const set2 = new Set(str2.split(" "));

  const intersection = new Set([...set1].filter((x) => set2.has(x)));
  const union = new Set([...set1, ...set2]);

  const similarity = intersection.size / union.size;

  return similarity;
};

const calculateWeightedSimilarity = function (
  article1Title: string,
  article1Summary: string,
  article2Title: string,
  article2Summary: string,
): number {
  const titleSim = calculateJaccardSimilarity(article1Title, article2Title);
  const summarySim = calculateJaccardSimilarity(
    article1Summary,
    article2Summary,
  );

  // 70% weight on title, 30% on summary
  return titleSim * TITLE_WEIGHT + summarySim * SUMMARY_WEIGHT;
};

function getCachedOrComputedHash(article: any, field: "title" | "summary") {
  const cached = hashCache[article.id];
  if (cached && cached[field]) {
    return cached[field];
  }

  let articleFieldValue = article[field];
  if (field === "title" && articleFieldValue === "") {
    const isBroadcastArticle = ["tv", "radio"].includes(article.media_type);
    if (isBroadcastArticle) {
      articleFieldValue = article.metadata?.broadcast?.program_name || "";
    }
  }

  const computed = computeHashText(articleFieldValue);
  if (!hashCache[article.id]) {
    hashCache[article.id] = { title: "", summary: "" };
  }
  hashCache[article.id][field] = computed;

  return computed;
}

function groupArticles(articles: any) {
  const groups = [];

  for (const article of articles) {
    article.published_on = normalizeUTCDateString(article.published_on);
    const articleTitle = getCachedOrComputedHash(article, "title");
    const articleSummary = getCachedOrComputedHash(article, "summary");

    let grouped = false;

    if (article.media_type !== "podcast") {
      for (const group of groups) {
        const groupTitle = getCachedOrComputedHash(group, "title");
        const groupSummary = getCachedOrComputedHash(group, "summary");

        const similarity = calculateWeightedSimilarity(
          articleTitle,
          articleSummary,
          groupTitle,
          groupSummary,
        );

        if (similarity >= GROUP_THRESHOLD) {
          group.nestedMentions.push({ isSubRow: true, ...article });
          grouped = true;
          break;
        }
      }
    }

    if (!grouped) {
      const newGroup = {
        nestedMentions: [{ isSubRow: true, ...article }],
        ...article,
      };
      groups.push(newGroup);
    }
  }

  // Post-process groups to set proper titles and obfuscation state
  for (const group of groups) {
    const firstNonObfuscated = group.nestedMentions.find(
      (mention: { is_obfuscated: boolean }) => !mention.is_obfuscated,
    );

    if (firstNonObfuscated) {
      group.title = firstNonObfuscated.title;
      group.summary = firstNonObfuscated.summary;
      group.is_obfuscated = false;
    } else {
      group.is_obfuscated = true;
    }
  }

  return groups;
}

export { groupArticles };
